import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { generateDataDogContext } from '../../utils/dataDog'
import {
  IGetOrdersOrder,
  IOrderLine,
} from '../../graphQL/queries/getOrders/types'
import { IOrderSummary } from '../../redux/slices/orders/types'
import { CheckoutRules } from '../../interfaces'

/**
 * Maps a `getOrders` response row onto the shape `OrderCard` / `OrderList`
 * consume.
 *
 * `itemsCount` is derived here because `Order` carries no such field — it is
 * the length of the selected lines. An order with no lines yields `0`, not
 * `undefined`.
 *
 * The lines themselves are kept rather than dropped after counting, because
 * reorder (COM-726) sends each one's `sku`, `quantity` and package name to
 * `prefillCart`. A line missing either `sku` or `quantity` is discarded:
 * `useAddPrefillCart` filters on `quantity > 0` and the mutation declares
 * both non-null, so such a line could only ever be dead weight.
 *
 * Response order is preserved rather than sorted. The user API fixes the
 * result newest-first and exposes no `sort` argument, so a client-side
 * re-sort would fight the server with nothing to signal the disagreement.
 * @internal
 */
const toOrderLines = (items?: IOrderLine[] | null): IOrderLine[] => {
  if (!Array.isArray(items)) return []

  return items.filter(
    (line): line is IOrderLine =>
      Boolean(line) &&
      typeof line.sku === 'string' &&
      line.sku.length > 0 &&
      typeof line.quantity === 'number',
  )
}

export const toOrderSummaries = (
  orders?: IGetOrdersOrder[] | null,
): IOrderSummary[] => {
  // The GraphQL contract declares `orders` as a nullable list of nullable
  // `Order`, so both the list and its entries are guarded before `.map`.
  if (!Array.isArray(orders)) return []

  return orders.filter(Boolean).map((order) => {
    const lines = toOrderLines(order.items)
    return {
      id: order.id,
      createdAt: order.createdAt ?? undefined,
      itemsCount: Array.isArray(order.items) ? order.items.length : 0,
      items: lines,
      total: order.total ?? undefined,
      status: order.status ?? undefined,
      sequence: order.sequence ?? undefined,
    }
  })
}

/**
 * Logs an order-history fetch failure to Datadog RUM with a structured
 * context, matching the `generateDataDogContext` + `datadogRum` pairing in
 * `hooks/summary/useGetSummary.ts`.
 *
 * For ApolloErrors it lifts `graphQLErrors[].extensions.code` and
 * `networkError.message` into separate facets so dashboards can split by
 * failure mode without parsing the raw error.
 *
 * The caller still owns the matching slice dispatch, because a first-page
 * failure and a load-more failure write different state.
 * @internal
 */
export const handleOrdersFetchError = (
  exception: unknown,
  functionName: string,
  title: string,
) => {
  const extraInfo: Record<string, unknown> = { function: functionName }

  if (exception instanceof ApolloError) {
    extraInfo.graphQLErrorCodes = exception.graphQLErrors?.map(
      (error) => error.extensions?.code,
    )
    extraInfo.graphQLErrorMessages = exception.graphQLErrors?.map(
      (error) => error.message,
    )
    extraInfo.networkErrorMessage = exception.networkError?.message
  }

  const context = generateDataDogContext({ title, extraInfo })
  datadogRum.startView(context.viewName)
  datadogRum.addError(exception, context)
}

/**
 * Whether a raised GraphQL failure declared the given code.
 *
 * **The code is a string, and this is the only way to read one.** The user API
 * does not report a failure as null data: a resolver returns its error through
 * `pushError`, which builds a `HeadlessError` with `` `${code}` `` in
 * `extensions.code`, and `graphql` re-throws any returned `Error`. Cinnamon's
 * Apollo client sets no `errorPolicy`, so Apollo's default `'none'` discards
 * `data` and surfaces the whole thing as an `ApolloError`. Classifying by
 * inspecting the payload is therefore not merely worse — it inspects a payload
 * that was thrown away.
 *
 * Compared without coercion. `pushError` interpolates, so a numeric code
 * cannot reach the wire, and accepting one here would only make a fixture that
 * hand-writes `code: 404` pass against a response the server cannot emit.
 * @internal
 */
export const hasGraphQLErrorCode = (
  exception: unknown,
  code: string,
): boolean =>
  exception instanceof ApolloError &&
  (exception.graphQLErrors ?? []).some(
    (error) => error.extensions?.code === code,
  )

/**
 * Whether a failure was the server saying "no such order".
 *
 * `404` covers "does not exist" and "not yours" alike, on purpose server-side
 * so a probe cannot confirm an id names a real order, so this says no more
 * than "not available to you".
 * @internal
 */
export const isNotFoundError = (exception: unknown): boolean =>
  hasGraphQLErrorCode(exception, '404')

/**
 * Whether the server rejected the request rather than failing to serve it.
 *
 * `400` is the definite half of the write flow's error space: the client
 * system declined a confirmation, a checkout rule was not satisfied, the
 * session is blocked, the payload did not validate. None of them is fixed by
 * waiting, which is what separates them from a `500` or a dropped connection —
 * those leave the outcome genuinely unknown and are worth polling for.
 * @internal
 */
export const isBadRequestError = (exception: unknown): boolean =>
  hasGraphQLErrorCode(exception, '400')

/**
 * The checkout rules a `createOrder` rejection names, or an empty list.
 *
 * `validateCheckoutSessionRule` puts the unsatisfied rules' `ruleType` values
 * into `ERROR.BAD_REQUEST`'s `extras`, which `pushError` copies onto
 * `extensions.extras`. That is the whole mechanism, and it runs inside
 * `getOrderSplitter` on the `createOrder` path — which is why enforcement only
 * reaches the client at all when the webview is the caller.
 *
 * **Filtered against the `CheckoutRules` enum, and that is load-bearing rather
 * than defensive.** `extras` is not exclusive to rule rejections: a joi
 * failure on the same mutation raises `BAD_REQUEST` with the *validation
 * messages* in `extras` (`'The payload has one or more invalid fields'` plus
 * `"'sessionUid' is required"` and friends). Without the filter a malformed
 * request would be reported to the shopper as a list of checkout rules to go
 * and satisfy, none of which exist. Matching on the description text instead
 * would pin a server-side string; matching on the enum pins the contract.
 *
 * Empty is the ordinary answer and means "not a rule rejection". Deduplicated
 * because several `graphQLErrors` entries can name the same rule.
 * @internal
 */
export const getFailedCheckoutRules = (exception: unknown): string[] => {
  if (!(exception instanceof ApolloError)) return []

  const known = Object.values(CheckoutRules) as string[]
  const named = (exception.graphQLErrors ?? []).flatMap((error) => {
    const { extras } = error.extensions ?? {}
    if (!Array.isArray(extras)) return []
    return extras.filter(
      (rule): rule is string =>
        typeof rule === 'string' && known.includes(rule),
    )
  })

  return Array.from(new Set(named))
}

/**
 * The server's own description of a failure, or undefined.
 *
 * `HeadlessError`'s message, which is the `InternalError`'s `description` — so
 * it arrives prefixed by its class (`'BAD REQUEST - order was not
 * confirmed'`). Kept verbatim, prefix included: it is a log and support
 * string, not shopper-facing copy, and trimming a known prefix would pin the
 * shape of the error dictionary from the far side of the API.
 *
 * Note this is **not** `extensions.message`, which is the *raw* thrown value's
 * `.message` and is undefined for every deliberate rejection here — a decline
 * throws the message as a bare string, whose `.message` is undefined, and a
 * rule rejection throws an `InternalError`, for which `pushError` records no
 * raw error at all.
 * @internal
 */
export const getServerErrorDescription = (
  exception: unknown,
): string | undefined => {
  if (!(exception instanceof ApolloError)) return undefined
  const [first] = exception.graphQLErrors ?? []
  return first?.message || undefined
}

/**
 * The one `400` from `confirmOrder` that is not definite.
 *
 * `confirmOrder`'s lookup filter is an **`$or`**:
 *
 * ```
 * { $or: [{ _id: id }, { sessionUid: objSessionUid }],
 *   status: { $in: [CREATED, SUGGESTED] } }
 * ```
 *
 * and it then demands `ordersLength === 1`. Because the clauses are unioned,
 * passing `id` cannot exclude the session's other confirmable orders — the
 * match set is `{the order with this _id} ∪ {every confirmable order in the
 * session}`. So a session holding two confirmable orders fails this check no
 * matter what the client sends, and `sessionUid` cannot simply be dropped to
 * narrow it: `validateConfirmOrder` requires it and the session middleware
 * needs it to scope the call to the caller.
 *
 * That state is reachable. `validateOrderCreated` blocks a second order only
 * while an existing one is `CREATED`, so an order sitting in `SUGGESTED` —
 * exactly what `validateDefaultClientOrder` produces when the client system
 * changes an order — does not block a new `CREATED` one beside it.
 *
 * The resulting rejection says nothing about whether *this* order can be
 * confirmed; it says the server could not tell which order was meant. Treating
 * it as a definite failure tells the shopper their order failed while both
 * orders sit in the database, so it belongs with the ambiguous half and polls.
 *
 * **Matched on the description, and that is a deliberate exception to the rule
 * `getFailedCheckoutRules` states.** `ordersLength` is attached to the server's
 * trace tags, not to `extensions`, so there is nothing else to key on. The
 * failure direction is what makes it acceptable: if the server ever reworded
 * the message, this returns `false` and the call reverts to being reported as
 * a terminal failure — the behaviour before this check existed, not a new one.
 * The fragment lives in `AMBIGUOUS_ORDER_MATCH_DESCRIPTION` so the one place it
 * has to be re-checked against the server is named rather than inline.
 * @internal
 */
export const AMBIGUOUS_ORDER_MATCH_DESCRIPTION = 'Only 1 order allowed'

export const isAmbiguousOrderMatchError = (exception: unknown): boolean =>
  isBadRequestError(exception) &&
  (getServerErrorDescription(exception) ?? '').includes(
    AMBIGUOUS_ORDER_MATCH_DESCRIPTION,
  )
